import ErrorPage from 'components/containers/ErrorPage';
import { NextPageContext } from 'next';
import React from 'react';
import { Store } from 'redux';
import { doFetchReport } from 'store/report/actions';
import { getActiveTilstandsRapport } from 'store/tilstandsrapportEntities/selectors';
import { getSjekkpunktExist } from 'store/tilstandsrapportEntities/sjekkpunkter/selectors';
import {
  getKontorId,
  getKontorNavn,
  getMeglerkjedeId,
} from 'store/tilstandsrapportEntities/tilstandsrapport/selectors';
import withError from './withError';

interface Props {
  tilstandsrapportId: string;
  itemId?: string;
  kontorId?: string;
  kontorNavn?: string;
  meglerkjedeId?: string;
  statusCode?: number;
  error: Error;
}

interface Context extends NextPageContext {
  reduxStore: Store;
}

function getDisplayName(WrappedComponent): string {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

function withTilstandsrapport(WrappedComponent): typeof WrappedComponent {
  class WithTilstandsrapport extends React.Component<Props> {
    static displayName = `WithTilstandsrapport(${getDisplayName(WrappedComponent)})`;

    static async getInitialProps(ctx: Context): Promise<Props | {}> {
      const { res, req, query, reduxStore } = ctx;
      const { tilstandsrapportId, itemId = null } = query;

      if (!tilstandsrapportId && res) {
        res.statusCode = 404;
        // https://github.com/zeit/next.js/blob/master/errors/empty-object-getInitialProps.md
        return {};
      }

      const { dispatch, getState } = reduxStore;
      const activeTilstandsrapportId = getActiveTilstandsRapport(getState());

      if (!activeTilstandsrapportId || activeTilstandsrapportId !== tilstandsrapportId) {
        try {
          // If req is set, that means we're on server side and has to forward cookies.
          // Nothing special has to be done on client side.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await dispatch<any>(doFetchReport(tilstandsrapportId.toString(), req && req.headers.cookie));
        } catch (e) {
          /* eslint-disable no-console */
          console.error(`Failed fetching tilstandsrapport`);
          console.error('Headers:');
          console.error(JSON.stringify(req.headers));
          console.error('Url:');
          console.error(req.url);
          console.error('Error:');
          console.error(JSON.stringify(e));
          console.error('Query:');
          console.error(JSON.stringify(query));
          throw e;
          /* eslint-disable no-console */
        }
      }

      if (itemId && res) {
        // If tilstandsrapportEntities.sjekkpunkter[id] doesn't exist, throw a 404.
        const sjekkpunktExist = getSjekkpunktExist(getState(), String(itemId));
        if (!sjekkpunktExist) {
          res.statusCode = 404;
          return {};
        }
      }

      let errorObj = null;
      if (res) {
        const {
          report: { error },
        } = getState();
        errorObj = error;
      }

      const state = getState();
      const kontorId = getKontorId(state, String(tilstandsrapportId));
      const kontorNavn = getKontorNavn(state, String(tilstandsrapportId));
      const meglerkjedeId = getMeglerkjedeId(state, String(tilstandsrapportId));

      // Make sure we run getInitialProps of components wrapped with this HOC.
      const wrappedComponentInitialProps = WrappedComponent.getInitialProps
        ? await WrappedComponent.getInitialProps(ctx)
        : {};

      // TODO: Rewrite this when we upgrade TypeScript to 3.7 to use nullish coalescing
      return {
        error: errorObj,
        statusCode: errorObj && errorObj.response ? errorObj.response.status : null,
        tilstandsrapportId: tilstandsrapportId.toString(),
        kontorId,
        kontorNavn,
        meglerkjedeId,
        itemId: itemId ? itemId.toString() : null,
        ...wrappedComponentInitialProps,
      };
    }

    render(): JSX.Element {
      const { tilstandsrapportId } = this.props;
      if (!tilstandsrapportId) return <ErrorPage statusCode={404} />;
      const { kontorId, kontorNavn, meglerkjedeId } = this.props;
      return (
        <>
          <div
            data-track-sbaid={tilstandsrapportId}
            {...(kontorId ? { 'data-track-kontorid': kontorId } : {})}
            {...(kontorNavn ? { 'data-track-kontornavn': kontorNavn } : {})}
            {...(meglerkjedeId ? { 'data-track-meglerkjedeid': meglerkjedeId } : {})}
          />
          <WrappedComponent {...this.props} />
        </>
      );
    }
  }
  return withError(WithTilstandsrapport);
}

export default withTilstandsrapport;
